import React, { useState, useEffect } from 'react';
import { Field } from 'react-final-form';
import { AnyObject } from 'final-form';
import { components } from 'react-select';
import { required } from 'Shared/utils/formValidations';
import { useSelector, useDispatch } from 'react-redux';

import { bulkCreateWorkspaces } from 'Shared/actions/workspaces';
import { useFetchWorkspaces, useDeepEqualSelector } from 'Shared/utils/hooks';
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';

import { formatWorkspaceTitle } from 'Shared/helpers';
import { getGroupWorkspaces, getOneOnOneWorkspacesWithBothUsers } from 'Shared/selectors/workspace';

import WpIcon from 'Wpds/components/icon/WpIcon';
import FormSelect from 'Shared/components/FormComponents/FormSelect';
import AddGroupModalWithCalendarIntegration from 'Shared/components/modals/AddGroupModalWithCalendarIntegration/AddGroupModalWithCalendarIntegration';
import CreateWorkspaceModal from 'Shared/components/CreateWorkspaceModal/CreateWorkspaceModal';
import TrackingConstants from 'Shared/constants/Tracking.constants';

import './WorkspaceField.scss';

type Props = {
  name: string;
  disabled?: boolean;
  oneOnOneUserId?: string;
};

type MenuProps = {
  children: React.ReactNode;
  onModalOpen: Function;
};

const customStyles = {
  menuList: base => ({ ...base, paddingTop: 0 }),
};

function CustomOption({ innerProps, data }: AnyObject) {
  const collaborationsCount = data.users.length;
  return (
    <div {...innerProps} className="CustomOption d-flex flex-column align-items-start px-3" data-name={data.name}>
      <div className="CustomOption__label">{data.label}</div>
      <div className="CustomOption__collaborations">{collaborationsCount} collaborators</div>
    </div>
  );
}

const CustomMenu = (props: MenuProps) => {
  const handleAddClick = () => props.onModalOpen();
  return (
    <components.Menu {...props}>
      <div className="CustomMenu__add_group d-flex py-2 px-3 cursor-pointer" onClick={handleAddClick}>
        <div className="mr-2">
          <WpIcon name="plus-circle" />
        </div>
        Create a new workspace
      </div>
      {props.children}
    </components.Menu>
  );
};

const WORKSPACE_SELECT_ID = 'WorkspaceField';

const WorkspaceField = ({ name, oneOnOneUserId = null, disabled = false }: Props) => {
  //Show all of the current user's group workspaces,
  // plus their 1:1 workspace with the teammate specified by oneOnOneUserId param if provided
  const dispatch = useDispatch();

  const [isLoading] = useFetchWorkspaces();
  const [isCreateWorkspaceModal, setIsCreatWorkspaceModalOpen] = useState(false);
  const [isCreateGroupOpen, setIsCreateGroupOpen] = useState(false);
  const { user: currentUser } = useCurrentUser();
  const oneOnOneWorkspaces = useSelector(state => getOneOnOneWorkspacesWithBothUsers(state));
  const oneOnOneWorkspace = oneOnOneWorkspaces.find(workspace =>
    workspace.users.find(user => user.id === oneOnOneUserId && user.id != currentUser.id)
  );
  const personalWorkspace = useSelector(state => state.workspaces).find(workspace => workspace.isPersonal);
  const members = useSelector(state => state.members);

  //Allow users to create a 1:1 workspace if a oneOnOneUserId param (other than current user themself)
  // is specified and the current user doesn't already have a 1:1 with that person
  // Otherwise, only display modal for creating a group workspace
  const allowOneOnOneCreate = oneOnOneUserId && !oneOnOneWorkspace && oneOnOneUserId != currentUser.id;

  const handleModalOpen = () => setIsCreatWorkspaceModalOpen(true);

  const handleModalClose = () => setIsCreatWorkspaceModalOpen(false);

  const handleGroupModalClose = () => {
    handleModalClose();
    setIsCreateGroupOpen(false);
  };

  const createOneOnOne = async onChange => {
    const oneOnOneUser = members.find(u => u.id === oneOnOneUserId);
    const workspacesParams = [{ name: oneOnOneUser.fullName, inviteeEmails: [oneOnOneUser.email] }];
    const firstCreatedWorkspace = await dispatch(
      bulkCreateWorkspaces({ workspacesParams, inviteMethod: TrackingConstants.INVITE_METHOD_TYPES.CREATE_UPDATE_GOAL })
    );
    onChange(firstCreatedWorkspace.id);
  };

  const options = useDeepEqualSelector(state => getGroupWorkspaces(state));

  if (oneOnOneWorkspace) {
    options.push(oneOnOneWorkspace);
  }
  if (oneOnOneUserId === currentUser.id) {
    options.push(personalWorkspace);
    oneOnOneWorkspaces.forEach(workspace => options.push(workspace));
  }

  options
    .filter(workspace => workspace)
    .forEach(workspace => (workspace.label = formatWorkspaceTitle({ workspace, user: currentUser })));

  useEffect(() => {
    document.getElementById(WORKSPACE_SELECT_ID).blur();
  }, [isCreateWorkspaceModal]);

  return (
    <div className="WorkspaceField">
      <Field name={name} validate={required}>
        {({ input: { value, onChange } }) => {
          return (
            <>
              <FormSelect
                isClearable
                isLoading={isLoading}
                inputId={WORKSPACE_SELECT_ID}
                options={options}
                validate={required}
                customStyles={customStyles}
                components={{
                  Option: CustomOption,
                  Menu: props => <CustomMenu {...props} onModalOpen={handleModalOpen} />,
                }}
                placeholder="Choose..."
                value={value}
                onChange={onChange}
                disabled={disabled}
              />
              {isCreateWorkspaceModal && allowOneOnOneCreate && (
                <CreateWorkspaceModal
                  isOpen={isCreateWorkspaceModal}
                  onSelectGroup={() => setIsCreateGroupOpen(true)}
                  onSelectOneOnOne={() => createOneOnOne(onChange)}
                  toggleModal={handleModalClose}
                />
              )}
              {((!allowOneOnOneCreate && isCreateWorkspaceModal) || isCreateGroupOpen) && (
                <AddGroupModalWithCalendarIntegration
                  withRedirect={false}
                  onSuccess={workspace => onChange(workspace.id)}
                  onClose={handleGroupModalClose}
                />
              )}
            </>
          );
        }}
      </Field>
    </div>
  );
};

export default WorkspaceField;
